import { createPermission } from '@backstage/plugin-permission-common';

/**
 * Permission to view unprocessed catalog entities.
 * This is an admin-only feature for troubleshooting catalog processing errors.
 */
export const catalogUnprocessedEntitiesPermission = createPermission({
  name: 'catalog.unprocessedEntities.view',
  attributes: { action: 'read' },
});

/**
 * Permission to read TechDocs content for a catalog entity.
 *
 * Uses the catalog-entity resource type so policies can reuse catalog conditions
 * (annotations, ownership, GitHub repo-team relations). This is intentionally
 * separate from catalog.entity.read so private entities can stay discoverable in
 * catalog/search while docs remain restricted.
 *
 * Name matches the forthcoming upstream permission (backstage/backstage#34437).
 */
export const techDocsEntityReadPermission = createPermission({
  name: 'techdocs.entity.read',
  attributes: { action: 'read' },
  resourceType: 'catalog-entity',
});

/**
 * Permission to view the Datadog Analytics page.
 * Restricted to the configured admin group (`app.admin.group`, defaulting to `group:default/backstage-admins`).
 */
export const datadogAnalyticsPermission = createPermission({
  name: 'datadog.analytics.view',
  attributes: { action: 'read' },
});

/**
 * Permission to view org data (divisions, platforms, and chapters).
 * This is a read-only permission available to all authenticated users.
 */
export const orgDataReadPermission = createPermission({
  name: 'org-data.read',
  attributes: { action: 'read' },
});

/**
 * Permission to create new divisions, platforms, or chapters.
 * Restricted to the configured admin group (`app.admin.group`, defaulting to `group:default/backstage-admins`).
 */
export const orgDataCreatePermission = createPermission({
  name: 'org-data.create',
  attributes: { action: 'create' },
});

/**
 * Permission to update existing divisions, platforms, or chapters.
 * Restricted to the configured admin group (`app.admin.group`, defaulting to `group:default/backstage-admins`).
 */
export const orgDataUpdatePermission = createPermission({
  name: 'org-data.update',
  attributes: { action: 'update' },
});

/**
 * Permission to delete divisions, platforms, or chapters.
 * Restricted to the configured admin group (`app.admin.group`, defaulting to `group:default/backstage-admins`).
 */
export const orgDataDeletePermission = createPermission({
  name: 'org-data.delete',
  attributes: { action: 'delete' },
});

/**
 * List of all custom app-level permissions.
 * Export this for use in the backend permission policy.
 */
export const appPermissions = [
  catalogUnprocessedEntitiesPermission,
  techDocsEntityReadPermission,
  datadogAnalyticsPermission,
  orgDataReadPermission,
  orgDataCreatePermission,
  orgDataUpdatePermission,
  orgDataDeletePermission,
];
